import type { Direction } from '@/@types/theme'

/**
 * The languages the app ships in.
 *
 * `label` is deliberately the language's own endonym, not its English name: a
 * user who cannot read the current interface language still needs to find
 * their own in the list, and "العربية" is legible to the person looking for it
 * in a way that "Arabic" is not.
 *
 * `dateFnLocale` is the dayjs locale bundle to load — dayjs ships these as
 * separate modules, so the key has to be the module name.
 */
export type LocaleCode = 'en' | 'fr' | 'es' | 'ar'

export type LocaleDefinition = {
    code: LocaleCode
    label: string
    englishLabel: string
    direction: Direction
    dayjsLocale: string
    /** BCP 47 tag for the `lang` attribute and `Intl` date formatting. */
    tag: string
    /**
     * The tag to format *numbers* with, which is not always `tag`.
     *
     * Arabic pins `nu-latn` deliberately. `Intl` defaults Arabic to
     * Arabic-Indic digits (١٢٣) while dayjs's `ar` locale formats dates with
     * Latin ones, and ApexCharts draws its axes in Latin digits regardless —
     * so following the default would put ٣ in a KPI tile directly above a
     * chart axis reading 3, for the same figure. One numeral system across the
     * page beats a locale-purer one that disagrees with the graph beside it.
     */
    numberTag: string
}

export const LOCALES: LocaleDefinition[] = [
    {
        code: 'en',
        label: 'English',
        englishLabel: 'English',
        direction: 'ltr',
        dayjsLocale: 'en',
        tag: 'en',
        numberTag: 'en',
    },
    {
        code: 'fr',
        label: 'Français',
        englishLabel: 'French',
        direction: 'ltr',
        dayjsLocale: 'fr',
        tag: 'fr',
        numberTag: 'fr',
    },
    {
        code: 'es',
        label: 'Español',
        englishLabel: 'Spanish',
        direction: 'ltr',
        dayjsLocale: 'es',
        tag: 'es',
        numberTag: 'es',
    },
    {
        code: 'ar',
        label: 'العربية',
        englishLabel: 'Arabic',
        direction: 'rtl',
        dayjsLocale: 'ar',
        tag: 'ar',
        numberTag: 'ar-u-nu-latn',
    },
]

export const LOCALE_CODES = LOCALES.map((locale) => locale.code)

export const DEFAULT_LOCALE: LocaleCode = 'en'

const LOCALE_BY_CODE = new Map(LOCALES.map((locale) => [locale.code, locale]))

export const getLocaleDefinition = (code: string): LocaleDefinition =>
    LOCALE_BY_CODE.get(code as LocaleCode) ??
    (LOCALE_BY_CODE.get(DEFAULT_LOCALE) as LocaleDefinition)

export const isSupportedLocale = (code: unknown): code is LocaleCode =>
    typeof code === 'string' && LOCALE_BY_CODE.has(code as LocaleCode)

/**
 * Narrow a browser language tag to something we ship.
 *
 * `navigator.language` is a full tag ("fr-CA", "ar-LB"), so match on the
 * primary subtag — a Lebanese Arabic speaker and an Egyptian one both get `ar`.
 */
export const resolveLocale = (candidate: unknown): LocaleCode => {
    if (isSupportedLocale(candidate)) {
        return candidate
    }

    if (typeof candidate === 'string') {
        const primary = candidate.split('-')[0]?.toLowerCase()
        if (isSupportedLocale(primary)) {
            return primary
        }
    }

    return DEFAULT_LOCALE
}
